import React, { useState, useEffect, useRef, ReactNode } from 'react';

interface LazyWidgetProps {
  children: ReactNode;
  height?: number; // Expected height in pixels for placeholder
  threshold?: number; // Intersection threshold (0-1)
  rootMargin?: string; // Root margin for intersection observer
  className?: string;
}

const LazyWidget: React.FC<LazyWidgetProps> = ({
  children,
  height = 300,
  threshold = 0.1,
  rootMargin = '50px',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasBeenVisible(true);
          // Once loaded, we can disconnect the observer
          observer.disconnect();
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin]);

  // Loading placeholder component
  const LoadingPlaceholder = () => (
    <div 
      className={`animate-pulse bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg shadow-md ${className}`}
      style={{ height: `${height}px` }}
    >
      <div className="p-4 h-full flex flex-col">
        {/* Widget header placeholder */}
        <div className="flex items-center justify-between mb-4">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
          <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
        
        {/* Widget content placeholder */}
        <div className="flex-1 space-y-3">
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-4/5"></div>
          <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/5"></div>
          
          {/* Additional placeholder elements for larger widgets */}
          {height > 200 && (
            <>
              <div className="h-16 bg-gray-300 dark:bg-gray-600 rounded w-full mt-4"></div>
              <div className="flex space-x-2">
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
                <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/4"></div>
              </div>
            </>
          )}
        </div>
        
        {/* Loading indicator */}
        <div className="flex items-center justify-center mt-4">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={elementRef} className={hasBeenVisible ? '' : className}>
      {hasBeenVisible ? children : <LoadingPlaceholder />}
    </div>
  );
};

export default LazyWidget; 